import { ObjectTarget } from "../../app/permissions";
import { ClassroomInfo } from "../classroom/classroomSlice";
import { assertImagesUrl, assertThumbnailUrl } from "../common";
import { SubjectInfo } from "../curriculum/model";
import { DptoInfo } from "../dpto/dptoSlice";
import { RoutineInfo } from "../routine/routineSlice";
import { SchoolInfo } from "../school/schoolSlice";
import { StudentInfo } from "../student/studentSlice";
import { UserInfo } from "../user/userSlice";

export class NamedObject {

    public _type: ObjectTarget;
    private obj : SchoolInfo | ClassroomInfo | StudentInfo | UserInfo | DptoInfo | RoutineInfo | SubjectInfo | undefined;

    static school(obj: SchoolInfo): NamedObject {
        return new NamedObject(ObjectTarget.SCHOOL, obj);
    }

    static classroom(obj: ClassroomInfo): NamedObject {
        return new NamedObject(ObjectTarget.CLASSROOM, obj);
    }

    static student(obj: StudentInfo): NamedObject {
        return new NamedObject(ObjectTarget.STUDENT, obj);
    }

    static user(obj: UserInfo): NamedObject {
        return new NamedObject(ObjectTarget.USER, obj);
    }

    static dpto(obj: DptoInfo): NamedObject {
        return new NamedObject(ObjectTarget.DPTO, obj);
    }

    static routine(obj: RoutineInfo): NamedObject {
        return new NamedObject(ObjectTarget.ROUTINE, obj);
    }

    static subject(obj: SubjectInfo): NamedObject {
        return new NamedObject(ObjectTarget.SUBJECT, obj);
    }

    static unknown(): NamedObject {
        return new NamedObject(ObjectTarget.UNKNOWN, undefined);
    }

    private constructor(
        type: ObjectTarget,
        obj: SchoolInfo | ClassroomInfo | StudentInfo | UserInfo | DptoInfo | RoutineInfo | SubjectInfo | undefined
    ) {
        this._type = type;
        this.obj = obj;
    }

    get id(): string {
        if( typeof this.obj === "object" ) {
            if("id" in this.obj) {
                return `${this.obj.id}`;
            }
            throw new Error("Object does not have an id property");
        }
        throw new Error("Invalid object type");
    }

    get targetId(): string {
        if (this._type === ObjectTarget.UNKNOWN || !this.obj) {
            return "unknown";
        }
        return `${this.id}`;
    }

    get type(): ObjectTarget {
        return this._type;
    }

    get display(): string {
        switch (this._type) {
            case ObjectTarget.SCHOOL: {
                const school = this.obj as SchoolInfo;
                return school.name || "Escola: " + school.id;
            }
            case ObjectTarget.CLASSROOM: {
                const classroom = this.obj as ClassroomInfo;
                return classroom.name || "Sala de Aula: " + classroom.id;
            }
            case ObjectTarget.STUDENT: {
                const student = this.obj as StudentInfo;
                return student.name || student.email || "Aluno: " + student.id;
            }
            case ObjectTarget.USER: {
                const user = this.obj as UserInfo;
                return user.name || user.email || "Usuário: " + user.id;
            }
            case ObjectTarget.DPTO: {
                const dpto = this.obj as DptoInfo;
                return dpto.name || "Departamento: " + dpto.id;
            }
            case ObjectTarget.ROUTINE: {
                const routine = this.obj as RoutineInfo;
                return routine.name || "Rotina: " + routine.id;
            }
            case ObjectTarget.SUBJECT: {
                const subject = this.obj as SubjectInfo;
                return subject.name || "Disciplina: " + subject.id;
            }
            case ObjectTarget.UNKNOWN: {
                return "Desconhecido";
            }
            default: {
                throw new Error("Unknown object type");
            }
        }
    }

    get image(): string | undefined {
        return assertImagesUrl(this.url);
    }

    get thumbnail(): string | undefined {
        return assertThumbnailUrl(this.url);
    }

    get url(): string | undefined{
        switch (this._type) {
            case ObjectTarget.SCHOOL: {
                const school = this.obj as SchoolInfo;
                return school.url;
            }
            case ObjectTarget.CLASSROOM: {
                const classroom = this.obj as ClassroomInfo;
                return classroom.url;
            }
            case ObjectTarget.STUDENT: {
                const student = this.obj as StudentInfo;
                return student.url;
            }
            case ObjectTarget.USER: {
                const user = this.obj as UserInfo;
                return user.url;
            }
            case ObjectTarget.DPTO: {
                const dpto = this.obj as DptoInfo;
                return dpto.url;
            }
            case ObjectTarget.ROUTINE: {
                const routine = this.obj as RoutineInfo;
                return routine.url;
            }
            case ObjectTarget.SUBJECT: {
                const subject = this.obj as SubjectInfo;
                return subject.url;
            }
            case ObjectTarget.UNKNOWN: {
                return undefined;
            }
            default: {
                throw new Error("Unknown object type");
            }
        }
    }

    get schoolId(): string | undefined{
        switch (this._type) {
            case ObjectTarget.SCHOOL: {
                const school = this.obj as SchoolInfo;
                return school.id;
            }
            case ObjectTarget.CLASSROOM: {
                const classroom = this.obj as ClassroomInfo;
                return classroom.schoolId;
            }
            case ObjectTarget.STUDENT: {
                const student = this.obj as StudentInfo;
                return student.schoolId;
            }
            case ObjectTarget.USER: {
                return undefined;
            }
            case ObjectTarget.DPTO: {
                const dpto = this.obj as DptoInfo;
                return dpto.schoolId;
            }
            case ObjectTarget.ROUTINE: {
                const routine = this.obj as RoutineInfo;
                return routine.schoolId;
            }
            case ObjectTarget.UNKNOWN: {
                return undefined;
            }
            default: {
                throw new Error("Unknown object type");
            }
        }
    }


}
